import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { Controller, useController, useForm } from "react-hook-form";
import { DatePicker, PickersDay } from "@mui/x-date-pickers";
import {
  BUTTON_CLEAR_DATES,
  BUTTON_SAVE,
  DATE_SHORT_2,
  END_DATE_PLACEHOLDER,
  PRESETS_DATE_PLACEHOLDER,
  SELECT_RANGE_DATE_PLACEHOLDER,
  START_DATE_PLACEHOLDER,
  BUTTON_START_NOW,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import { APP_DROPDOWN_Z_INDEX } from "../../utils/modalLayer";
import Popover from "@mui/material/Popover";
import { useEffect, useMemo, useRef, useState } from "react";
import Button from "../Buttons/Button";
import { addWeeks, isAfter } from "date-fns";
import IconButton from "../Buttons/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Typography } from "@mui/material";
import Select from "./Select";
import { DATE_RANGE, DATE_RANGE_PRESETS } from "../../utils/constants";
import {
  startOfToday,
  endOfToday,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMonths,
  subYears,
  startOfDay,
  endOfDay,
} from "date-fns";

const buildPresetRanges = () => {
  const today = new Date();
  const weekOpts = { weekStartsOn: 1 };

  return {
    [DATE_RANGE.today]: { start: startOfToday(), end: endOfToday() },
    [DATE_RANGE.yesterday]: {
      start: startOfDay(subDays(today, 1)),
      end: endOfDay(subDays(today, 1)),
    },
    [DATE_RANGE.last_7]: {
      start: startOfDay(subDays(today, 6)),
      end: endOfToday(),
    },
    [DATE_RANGE.last_14]: {
      start: startOfDay(subDays(today, 13)),
      end: endOfToday(),
    },
    [DATE_RANGE.last_30]: {
      start: startOfDay(subDays(today, 29)),
      end: endOfToday(),
    },
    [DATE_RANGE.this_week]: {
      start: startOfWeek(today, weekOpts),
      end: endOfToday(),
    },
    [DATE_RANGE.last_week]: {
      start: startOfWeek(subDays(today, 7), weekOpts),
      end: endOfWeek(subDays(today, 7), weekOpts),
    },
    [DATE_RANGE.this_month]: {
      start: startOfMonth(today),
      end: endOfToday(),
    },
    [DATE_RANGE.last_month]: {
      start: startOfMonth(subMonths(today, 1)),
      end: endOfMonth(subMonths(today, 1)),
    },
    [DATE_RANGE.this_year]: {
      start: startOfYear(today),
      end: endOfToday(),
    },
    [DATE_RANGE.last_year]: {
      start: startOfYear(subYears(today, 1)),
      end: endOfYear(subYears(today, 1)),
    },
  };
};

const DATE_PICKER_POPPER_PROPS = {
  placement: "bottom-start",
  strategy: "fixed",
  sx: {
    zIndex: APP_DROPDOWN_Z_INDEX,
    "& .MuiPaper-root": {
      maxHeight: "calc(100vh - 16px)",
      overflowY: "auto",
    },
  },
  modifiers: [
    {
      name: "flip",
      enabled: true,
      options: {
        fallbackPlacements: ["top-start", "bottom-start"],
        rootBoundary: "viewport",
        altBoundary: true,
      },
    },
    {
      name: "preventOverflow",
      enabled: true,
      options: {
        boundary: "viewport",
        rootBoundary: "viewport",
        altAxis: true,
        padding: 8,
        tether: false,
      },
    },
    {
      name: "offset",
      enabled: true,
      options: { offset: [0, 8] },
    },
    {
      name: "computeStyles",
      options: { adaptive: false },
    },
  ],
};

const RANGE_DATE_POPOVER_POPPER_PROPS = {
  strategy: "fixed",
  modifiers: [
    {
      name: "flip",
      enabled: true,
      options: {
        fallbackPlacements: ["bottom", "top"],
        rootBoundary: "viewport",
      },
    },
    {
      name: "preventOverflow",
      enabled: true,
      options: {
        boundary: "viewport",
        rootBoundary: "viewport",
        padding: 8,
      },
    },
    {
      name: "computeStyles",
      options: { adaptive: false },
    },
  ],
};

const DateRangeFields = ({
  control,
  name,
  errors,
  fullWidth,
  variant,
  sxField,
  startDateTime,
  endDateTime,
  error,
  isTextMode = false,
  onSave,
  tempStartDate,
  tempEndDate,
  onTempDateChange,
  minDate,
  maxDate,
  clearable = false,
  showClearButton = false,
  onClear = () => {},
  renderDay = null,
  showStartNowButton = false,
  onPresetChange,
  controlPreset,
  preset,
}) => {
  const { t } = useTranslationApp();
  const refIcon = useRef(null);
  const refIcon2 = useRef(null);
  const currentStartDate =
    tempStartDate !== undefined ? tempStartDate : startDateTime;
  const currentEndDate = tempEndDate !== undefined ? tempEndDate : endDateTime;
  const effectiveMinDate = minDate;
  const effectiveMaxDate = maxDate;

  const presetOptions = useMemo(() => DATE_RANGE_PRESETS, []);
  const isCustomPreset = preset === DATE_RANGE.custom;
  const presetRanges = useMemo(() => buildPresetRanges(), []);

  const onSelectPreset = (presetKey, field) => {
    const range = presetRanges[presetKey];
    if (presetKey === DATE_RANGE.custom) {
      if (onPresetChange && !isTextMode) {
        onPresetChange(DATE_RANGE.custom);
      }
      const newValue = {
        ...field.value,
        startDateTime: null,
        endDateTime: null,
      };
      if (isTextMode) {
        onTempDateChange({ start: null, end: null });
      } else {
        field.onChange(newValue);
      }
      return;
    }
    if (!range) return;

    if (onPresetChange && !isTextMode) {
      onPresetChange(presetKey);
    }

    const clampedStart =
      effectiveMinDate && range.start && range.start < effectiveMinDate
        ? effectiveMinDate
        : range.start;
    const clampedEnd =
      effectiveMaxDate && range.end && range.end > effectiveMaxDate
        ? effectiveMaxDate
        : range.end;

    const wouldBeInvalid =
      clampedStart && clampedEnd && clampedStart > clampedEnd;
    const finalStart = wouldBeInvalid ? range.start : clampedStart;
    const finalEnd = wouldBeInvalid ? range.end : clampedEnd;

    if (isTextMode) {
      onTempDateChange({ start: finalStart, end: finalEnd });
    } else {
      field.onChange({
        ...field.value,
        startDateTime: finalStart,
        endDateTime: finalEnd,
      });
    }
  };

  // Función para renderizar días personalizados
  const computedRenderDay = (day, _value, DayComponentProps) => {
    if (renderDay) return renderDay(day, _value, DayComponentProps);
    let isDisabled = false;
    if (effectiveMinDate && day < effectiveMinDate) isDisabled = true;
    if (effectiveMaxDate && day > effectiveMaxDate) isDisabled = true;
    return (
      <PickersDay
        {...DayComponentProps}
        disabled={isDisabled}
        sx={{ opacity: isDisabled ? 0.35 : 1 }}
      />
    );
  };

  const handleStartDateChange = (date, field) => {
    if (isTextMode) {
      // If there is an end date and the new start date is after it
      if (currentEndDate && isAfter(date, currentEndDate)) {
        const oneWeekLater = addWeeks(date, 1);
        // If one week later exceeds the maximum date, use the maximum date
        const newEndDate = isAfter(oneWeekLater, effectiveMaxDate)
          ? effectiveMaxDate
          : oneWeekLater;

        onTempDateChange({ start: date, end: newEndDate });
      } else {
        onTempDateChange({ start: date, end: currentEndDate });
      }
    } else {
      // If there is an end date and the new start date is after it
      if (currentEndDate && isAfter(date, currentEndDate)) {
        const oneWeekLater = addWeeks(date, 1);
        // If one week later exceeds the maximum date, use the maximum date
        const newEndDate = isAfter(oneWeekLater, effectiveMaxDate)
          ? effectiveMaxDate
          : oneWeekLater;

        field.onChange({
          ...field.value,
          startDateTime: date,
          endDateTime: newEndDate,
        });
      } else {
        field.onChange({
          ...field.value,
          startDateTime: date,
        });
      }
    }
  };

  const onClickStartNow = (field) => {
    const now = new Date();

    if (isTextMode) {
      // If there is an end date and the new start date is after it
      if (currentEndDate && isAfter(now, currentEndDate)) {
        const oneWeekLater = addWeeks(now, 1);
        // If one week later exceeds the maximum date, use the maximum date
        const newEndDate = isAfter(oneWeekLater, effectiveMaxDate)
          ? effectiveMaxDate
          : oneWeekLater;

        onTempDateChange({ start: now, end: newEndDate });
      } else {
        onTempDateChange({ start: now, end: currentEndDate });
      }
    } else {
      // If there is an end date and the new start date is after it
      if (currentEndDate && isAfter(now, currentEndDate)) {
        const oneWeekLater = addWeeks(now, 1);
        // If one week later exceeds the maximum date, use the maximum date
        const newEndDate = isAfter(oneWeekLater, effectiveMaxDate)
          ? effectiveMaxDate
          : oneWeekLater;

        field.onChange({
          ...field.value,
          startDateTime: now,
          endDateTime: newEndDate,
        });
      } else {
        field.onChange({
          ...field.value,
          startDateTime: now,
        });
      }
    }
  };

  const isNowValidForStartNow = () => {
    const now = new Date();

    // Si hay una fecha mínima y el now está antes de ella, no es válido
    if (effectiveMinDate && now < effectiveMinDate) {
      return false;
    }

    // Si hay una fecha máxima y el now está después de ella, no es válido
    if (effectiveMaxDate && now > effectiveMaxDate) {
      return false;
    }

    return true;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isTextMode ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        p: isTextMode ? 2 : 0,
        pt: isTextMode ? 3 : 0,
        position: "relative",
      }}
    >
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              position: "relative",
            }}
          >
            {controlPreset && (
              <Box sx={{ mb: 1 }}>
                <Select
                  variant="filled"
                  control={controlPreset}
                  name="datePreset"
                  options={presetOptions}
                  fullWidth
                  doTranslate={true}
                  useOnChange={true}
                  actionsChange={(value) => onSelectPreset(value, field)}
                  showEmptyOption={true}
                  label={t(PRESETS_DATE_PLACEHOLDER)}
                  sx={{
                    width: 284,
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                  }}
                />
              </Box>
            )}
            {(!controlPreset || isCustomPreset) && (
              <DatePicker
                onBlur={field.onBlur}
                onChange={(date) => handleStartDateChange(date, field)}
                label={t(START_DATE_PLACEHOLDER)}
                value={currentStartDate}
                errors={errors}
                fullWidth={fullWidth}
                variant={variant}
                renderDay={computedRenderDay}
                InputAdornmentProps={{
                  position: "start",
                  ref: refIcon,
                }}
                {...(showStartNowButton &&
                  isNowValidForStartNow() && {
                    components: {
                      ActionBar: ({ onAccept }) => (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            pb: 2,
                            px: 2,
                          }}
                        >
                          <Button
                            onClick={() => {
                              onClickStartNow(field);
                              onAccept();
                            }}
                            size="small"
                            color="secondary"
                          >
                            {t(BUTTON_START_NOW)}
                          </Button>
                        </Box>
                      ),
                    },
                  })}
                PopperProps={DATE_PICKER_POPPER_PROPS}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth={fullWidth}
                    variant={variant}
                    error={!!error}
                    onClick={() => {
                      const child = refIcon?.current?.children?.[1];
                      if (child) {
                        child?.click();
                      }
                    }}
                    sx={{
                      ...sxField,
                      color: "fields.background",
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                    }}
                  />
                )}
              />
            )}
            {clearable && (
              <IconButton
                onClick={() => {
                  field.onChange({
                    ...field.value,
                    startDateTime: null,
                  });
                }}
                sx={{
                  position: "absolute",
                  right: 24,
                  top: 24,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        )}
      />
      {(!isTextMode || (controlPreset && isCustomPreset)) && (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                position: "relative",
              }}
            >
              <DatePicker
                onBlur={field.onBlur}
                onChange={(date) => {
                  if (isTextMode) {
                    onTempDateChange({ start: currentStartDate, end: date });
                  } else {
                    field.onChange({
                      ...field.value,
                      endDateTime: date,
                    });
                  }
                }}
                label={t(END_DATE_PLACEHOLDER)}
                value={currentEndDate}
                disabled={!currentStartDate}
                errors={errors}
                fullWidth={fullWidth}
                variant={variant}
                renderDay={computedRenderDay}
                InputAdornmentProps={{
                  position: "start",
                  ref: refIcon2,
                }}
                PopperProps={DATE_PICKER_POPPER_PROPS}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth={fullWidth}
                    variant={variant}
                    error={!!error}
                    onClick={() => {
                      const child = refIcon2?.current?.children?.[1];
                      if (child) {
                        child?.click();
                      }
                    }}
                    sx={{
                      ...sxField,
                      color: "fields.background",
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                    }}
                  />
                )}
              />
              {clearable && (
                <IconButton
                  onClick={() => {
                    field.onChange({
                      ...field.value,
                      endDateTime: null,
                    });
                  }}
                  sx={{ position: "absolute", right: 24, top: 24 }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          )}
        />
      )}
      {isTextMode && (
        <Button
          color="secondary"
          onClick={onSave}
          disabled={!currentStartDate || !currentEndDate}
          fullWidth
        >
          {t(BUTTON_SAVE)}
        </Button>
      )}
      {showClearButton && isTextMode && (
        <Button color="secondary" onClick={onClear} fullWidth>
          {t(BUTTON_CLEAR_DATES)}
        </Button>
      )}
    </Box>
  );
};

const RangeDateField = ({
  control,
  name,
  errors = {},
  fullWidth = true,
  variant = "filled",
  sx = {},
  sxField = {},
  mode = "form",
  minDate,
  maxDate,
  onSave = () => {},
  clearable = false,
  defaultPlaceholderText = SELECT_RANGE_DATE_PLACEHOLDER,
  showClearButton = false,
  showIconText = false,
  startIcon = null,
  sxText = {},
  renderDay = null,
  showStartNowButton = false,
  initialPresetKey = null,
  onPresetChange,
}) => {
  const {
    field: { value, onChange },
  } = useController({
    control,
    name,
  });

  const { t } = useTranslationApp();
  const error = getReactHookFormNestedError(errors, name);

  const startDateTime = value.startDateTime || null;
  const endDateTime = value.endDateTime || null;

  const [tempDates, setTempDates] = useState({
    start: startDateTime,
    end: endDateTime,
  });

  const popoverAnchorRef = useRef(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const {
    control: controlPreset,
    watch: watchPreset,
    setValue: setPresetValue,
  } = useForm({
    defaultValues: { datePreset: initialPresetKey || "" },
  });
  const preset = watchPreset("datePreset");

  useEffect(() => {
    setPresetValue("datePreset", initialPresetKey || "");
  }, [initialPresetKey, setPresetValue]);

  useEffect(() => {
    if (!initialPresetKey || initialPresetKey === "none") return;
    const hasStart = !!startDateTime;
    const hasEnd = !!endDateTime;
    if (hasStart || hasEnd) return;

    const ranges = buildPresetRanges();
    const range = ranges[initialPresetKey];
    if (!range) return;

    onChange({
      startDateTime: range.start,
      endDateTime: range.end,
    });
  }, [initialPresetKey, startDateTime, endDateTime, onChange]);

  const onSaveForm = () => {
    onChange({
      startDateTime: tempDates.start,
      endDateTime: tempDates.end,
    });
    if (onPresetChange) {
      onPresetChange(preset || null, {
        startDate: tempDates.start?.getTime?.() ?? null,
        endDate: tempDates.end?.getTime?.() ?? null,
      });
    }
    setIsPopoverOpen(false);
    onSave();
  };

  const onClear = () => {
    onChange({
      startDateTime: null,
      endDateTime: null,
    });
    setTempDates({
      start: null,
      end: null,
    });

    setPresetValue("datePreset", "");

    if (onPresetChange) {
      onPresetChange(null, { startDate: null, endDate: null });
    }
  };

  const onClose = () => {
    setTempDates({
      start: startDateTime,
      end: endDateTime,
    });
    setIsPopoverOpen(false);
  };

  const onClosePopover = () => onClose();
  const onToggleDatePicker = () => setIsPopoverOpen((prev) => !prev);

  useEffect(() => {
    setTempDates({
      start: startDateTime,
      end: endDateTime,
    });
  }, [startDateTime, endDateTime]);

  if (mode === "text") {
    const dateText =
      startDateTime && endDateTime
        ? `${t(DATE_SHORT_2, { date: startDateTime })} - ${t(DATE_SHORT_2, {
            date: endDateTime,
          })}`
        : t(defaultPlaceholderText);

    return (
      <>
        <Box
          ref={popoverAnchorRef}
          onClick={onToggleDatePicker}
          data-tooltip-trigger="true"
          sx={{
            cursor: "pointer",
            ...sx,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {startIcon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1,
                lineHeight: 0,
              }}
            >
              {startIcon}
            </Box>
          )}
          <Typography sx={{ ...sxText }}>{dateText}</Typography>
          {showIconText && <KeyboardArrowDownIcon />}
        </Box>
        <Popover
          open={isPopoverOpen}
          onClose={onClosePopover}
          anchorEl={popoverAnchorRef.current}
          marginThreshold={16}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          PopperProps={RANGE_DATE_POPOVER_POPPER_PROPS}
          PaperProps={{
            sx: {
              p: 0,
              borderRadius: 3,
              mt: "8px !important",
              backgroundColor: "background.paper",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              overflow: "visible",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -6,
                left: "50%",
                transform: "translateX(-50%) rotate(45deg)",
                width: 12,
                height: 12,
                backgroundColor: "background.paper",
                borderLeft: `1px solid ${INPUT_COLOR_BORDER}`,
                borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
                borderBottom: `none`,
                borderRight: `none`,
                zIndex: 0,
              },
            },
          }}
        >
          <Box>
            <DateRangeFields
              control={control}
              name={name}
              errors={errors}
              fullWidth={fullWidth}
              variant={variant}
              sxField={sxField}
              startDateTime={startDateTime}
              endDateTime={endDateTime}
              error={error}
              isTextMode={true}
              onSave={onSaveForm}
              tempStartDate={tempDates.start}
              tempEndDate={tempDates.end}
              onTempDateChange={setTempDates}
              onClose={onClose}
              minDate={minDate}
              maxDate={maxDate}
              clearable={clearable}
              showClearButton={showClearButton}
              onClear={onClear}
              startIcon={startIcon}
              renderDay={renderDay}
              showStartNowButton={showStartNowButton}
              onPresetChange={onPresetChange}
              controlPreset={controlPreset}
              preset={preset}
            />
          </Box>
        </Popover>
      </>
    );
  }

  return (
    <Box sx={sx}>
      <DateRangeFields
        control={control}
        name={name}
        errors={errors}
        fullWidth={fullWidth}
        variant={variant}
        sxField={sxField}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        error={error}
        clearable={clearable}
        renderDay={renderDay}
        showStartNowButton={showStartNowButton}
      />
    </Box>
  );
};

export default RangeDateField;
